/**
 * useSgpFilterState — result filter + pagination state for SgpFlowScreen.
 * Extracted verbatim from SgpFlowScreen.jsx (god-file decomposition).
 * Owns: sportsbook/market/player/result-type/line-type/leg-count filters,
 * +EV-only toggle (synced from profile preference), search text, pagination.
 */

import { useState, useEffect } from 'react';

export default function useSgpFilterState({ userProfile }) {
    const [currentPage, setCurrentPage] = useState(1); // Pagination: current page
    const itemsPerPage = userProfile?.preferences?.legsPerPage || 10;
    const [filterSportsbooks, setFilterSportsbooks] = useState([]);
    const [filterMarkets, setFilterMarkets] = useState([]);
    const [filterPlayers, setFilterPlayers] = useState([]); // Player names (from player props in results)
    const [filterResultType, setFilterResultType] = useState('all'); // 'all' | 'team' | 'props' - filter by result type
    const [filterDeepLinksOnly, setFilterDeepLinksOnly] = useState(false); // Only show bets with deep links
    // Hide items whose header EV is ≤ 0. Initial value comes from the user's
    // saved preference (`preferences.filterPositiveEvOnly`), so flipping it in
    // the settings page controls the per-session default. The inline toolbar
    // button still toggles it mid-session without overwriting the saved pref.
    const [filterPositiveEvOnly, setFilterPositiveEvOnly] = useState(
        !!userProfile?.preferences?.filterPositiveEvOnly
    );
    // Sync if preferences hydrate async (first render often before profile loads).
    useEffect(() => {
        if (userProfile?.preferences?.filterPositiveEvOnly !== undefined) {
            setFilterPositiveEvOnly(!!userProfile.preferences.filterPositiveEvOnly);
        }
    }, [userProfile?.preferences?.filterPositiveEvOnly]);
    const [filterLineType, setFilterLineType] = useState('all'); // 'all' | 'main' | 'alt' - for player props
    const [filterLegCount, setFilterLegCount] = useState(null); // null = all, or number (2, 3, 4…) for SGP mode
    const [searchText, setSearchText] = useState('');

    // Toggle filters
    const toggleSportsbookFilter = (book) => {
        setFilterSportsbooks(prev => 
            prev.includes(book) ? prev.filter(b => b !== book) : [...prev, book]
        );
        setCurrentPage(1); // Reset to first page when filtering
    };

    const toggleMarketFilter = (market) => {
        setFilterMarkets(prev => 
            prev.includes(market) ? prev.filter(m => m !== market) : [...prev, market]
        );
        setCurrentPage(1); // Reset to first page when filtering
    };

    const togglePlayerFilter = (playerName) => {
        setFilterPlayers(prev => 
            prev.includes(playerName) ? prev.filter(p => p !== playerName) : [...prev, playerName]
        );
        setCurrentPage(1);
    };

    return {
        currentPage, setCurrentPage, itemsPerPage,
        filterSportsbooks, setFilterSportsbooks,
        filterMarkets, setFilterMarkets,
        filterPlayers, setFilterPlayers,
        filterResultType, setFilterResultType,
        filterDeepLinksOnly, setFilterDeepLinksOnly,
        filterPositiveEvOnly, setFilterPositiveEvOnly,
        filterLineType, setFilterLineType,
        filterLegCount, setFilterLegCount,
        searchText, setSearchText,
        toggleSportsbookFilter,
        toggleMarketFilter,
        togglePlayerFilter,
    };
}
